import type { CatalogOption, Stay } from '../../../reserva/types'
import { IDENTIFIER_TYPE, spainId } from '../../rules'
import type { GuestData, GuestField } from '../../types'
import type { MiDniQrLocalData } from './midniQrPayload'

/**
 * Normalización de la lectura de MiDNI. Portada de la app actual (MiDniQrCapture.tsx `toMiDniExtractedData`
 * y midniQrData.ts `normalizeMiDniGuest`) al modelo de v2: mismas reglas de emparejado de provincia,
 * municipio (E8), país, tipo de documento y sexo, y la misma lista de campos obligatorios.
 * `getMiDniResponseAction`, `getMiDniRequiredGuestFields` y `createMiDniManualGuest` no se portan: solo
 * servían al endpoint E6, que la app actual nunca llama (inventario §2.0).
 */

export interface MiDniRawData {
  name?: string | undefined
  firstSurname?: string | undefined
  secondSurname?: string | undefined
  surname?: string | undefined
  personalNumber?: string | undefined
  documentType?: string | undefined
  dateOfExpiry?: string | undefined
  nationality?: string | undefined
  dateOfBirth?: string | undefined
  gender?: string | undefined
  address?: string | undefined
  city?: string | undefined
  cityOfBirth?: string | undefined
  documentNumber?: string | undefined
  issuingState?: string | undefined
  region?: string | undefined
  verified?: boolean | undefined
}

export interface MiDniExtractedData {
  reserveId?: string | undefined
  name?: string | undefined
  surname1?: string | undefined
  surname2?: string | undefined
  identifier?: string | undefined
  identifierTypeId?: string | undefined
  expeditionDate?: string | undefined
  countryId?: string | undefined
  provinceId?: string | undefined
  bornDate?: string | undefined
  sexId?: string | undefined
  verifiedData?: boolean | undefined
  rawData?: MiDniRawData | undefined
  supportNumber?: string | undefined
  address?: string | undefined
  city?: string | undefined
  municipalityId?: string | undefined
  postalCode?: string | undefined
  email?: string | undefined
}

const normalize = (value?: string) => value?.trim().toLocaleLowerCase()

export const splitSurnames = (surname?: string) => {
  const [surname1, ...rest] = surname?.trim().split(/\s+/) ?? []
  return { surname1, surname2: rest.join(' ') || undefined }
}

/** MiDniQrCapture.toMiDniExtractedData */
export function toMiDniExtractedData(data: MiDniQrLocalData, reserveId: string): MiDniExtractedData {
  const { surname1, surname2 } = splitSurnames(data.surname)
  return {
    reserveId,
    name: data.name,
    surname1,
    surname2,
    identifier: data.documentNumber,
    expeditionDate: data.expirationDate,
    bornDate: data.bornDate,
    sexId: data.gender,
    supportNumber: data.supportNumber,
    address: data.address,
    city: data.addressLine1,
    verifiedData: false,
    rawData: {
      name: data.name,
      firstSurname: surname1,
      secondSurname: surname2,
      surname: data.surname,
      personalNumber: data.documentNumber,
      documentType: 'IDCard',
      dateOfExpiry: data.expirationDate,
      nationality: 'ESP',
      dateOfBirth: data.bornDate,
      gender: data.gender,
      address: data.address,
      city: data.addressLine1,
      cityOfBirth: data.birthPlace,
      documentNumber: data.supportNumber,
      issuingState: 'ESP',
      region: data.addressLine2,
      verified: false,
    },
  }
}

type Catalogs = Stay['registration']['catalogs']

function findSexId(value: string | undefined, rawGender: string | undefined, catalogs: Catalogs) {
  const candidates = [value, rawGender].map(normalize).filter((c): c is string => Boolean(c))
  const male = ['m', 'male', 'hombre']
  const female = ['f', 'female', 'mujer']
  const isMale = candidates.some((c) => male.includes(c))
  const isFemale = candidates.some((c) => female.includes(c))
  return catalogs.sexes.find(({ id, name, value: v }) => {
    const opts = [id, name, v].map(normalize)
    return opts.some((o) => o !== undefined && candidates.includes(o)) || (isMale && opts.some((o) => o !== undefined && male.includes(o))) || (isFemale && opts.some((o) => o !== undefined && female.includes(o)))
  })?.id
}

function findIdentifierTypeId(data: MiDniExtractedData, catalogs: Catalogs) {
  const exact = catalogs.identifierTypes.find(({ id }) => id === data.identifierTypeId)
  if (exact) return exact.id
  if (normalize(data.rawData?.documentType) === 'idcard') {
    return catalogs.identifierTypes.find(({ id, name, value }) => id === IDENTIFIER_TYPE.dni || [name, value].some((c) => ['dni', 'identity', 'identidad'].some((w) => normalize(c)?.includes(w))))?.id
  }
  return undefined
}

/** `dd-MM-yyyy` → `yyyy-MM-dd` (MiDNI da las fechas como la API del PMS). */
const toIso = (d?: string) => {
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(d ?? '')
  return m ? `${m[3]}-${m[2]}-${m[1]}` : undefined
}

export async function normalizeMiDniGuest({
  extractedData,
  currentGuest,
  stay,
  getMunicipalities,
}: {
  extractedData: MiDniExtractedData
  currentGuest: GuestData
  stay: Pick<Stay, 'registration'>
  getMunicipalities: (provinceId: string) => Promise<CatalogOption[]>
}): Promise<{ guest: GuestData; missingFields: GuestField[] }> {
  const catalogs = stay.registration.catalogs
  const province = catalogs.provinces.find(({ id, name, value }) => id === extractedData.provinceId || [name, value].some((c) => normalize(c) === normalize(extractedData.rawData?.region)))
  let municipality: CatalogOption | undefined
  if (province) {
    try {
      const list = await getMunicipalities(province.id)
      municipality = list.find(({ id, value }) => id === extractedData.municipalityId || normalize(value) === normalize(extractedData.city ?? extractedData.rawData?.city))
    } catch {
      municipality = undefined
    }
  }
  const countryId = catalogs.countries.some(({ id }) => id === extractedData.countryId) ? (extractedData.countryId ?? currentGuest.countryId) : currentGuest.countryId
  const guest: GuestData = {
    ...currentGuest,
    name: extractedData.name ?? currentGuest.name,
    surname1: extractedData.surname1 ?? currentGuest.surname1,
    surname2: extractedData.surname2 ?? currentGuest.surname2,
    identifier: extractedData.identifier ?? currentGuest.identifier,
    identifierTypeId: findIdentifierTypeId(extractedData, catalogs) ?? currentGuest.identifierTypeId,
    supportNumber: extractedData.supportNumber ?? currentGuest.supportNumber,
    countryId,
    provinceId: province?.id ?? currentGuest.provinceId,
    municipalityId: municipality?.id ?? currentGuest.municipalityId,
    city: extractedData.city ?? extractedData.rawData?.city ?? currentGuest.city,
    address: extractedData.address ?? extractedData.rawData?.address ?? currentGuest.address,
    postalCode: extractedData.postalCode ?? currentGuest.postalCode,
    bornDate: toIso(extractedData.bornDate) ?? currentGuest.bornDate,
    email: extractedData.email ?? currentGuest.email,
    sexId: findSexId(extractedData.sexId, extractedData.rawData?.gender, catalogs) ?? currentGuest.sexId,
  }

  const required: GuestField[] = ['name', 'surname1', 'identifier', 'identifierTypeId', 'countryId', 'bornDate', 'sexId', 'email']
  const isSpain = countryId !== '' && countryId === spainId(stay)
  const isSpanishId = isSpain && guest.identifierTypeId === IDENTIFIER_TYPE.dni
  if (isSpain) required.push('provinceId')
  if (isSpanishId) required.push('surname2')
  if (stay.registration.rules.ministerioInterior) {
    required.push('address', 'postalCode')
    if (isSpain) required.push('municipalityId')
    if (isSpanishId) required.push('supportNumber')
  }
  const missingFields = required.filter((f) => f !== 'relationship' && !guest[f])
  return { guest, missingFields }
}
